'use client';

import { AlertCircle, RefreshCw } from 'lucide-react';
import { catchError, type ErrorInfo } from 'next/error';

import { Button } from '@/components/ui/button';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { useReportError } from '@/hooks/use-report-error';
import { cn } from '@/lib/utils';
import { deriveErrorDigest } from '@/lib/utils/derive-error-digest';

const ErrorFallback = (
  {
    message,
    className,
    isSilent,
  }: { message?: string; className?: string; isSilent?: boolean },
  { error, retry }: ErrorInfo,
) => {
  useReportError({ error });

  if (isSilent) return null;

  const digest = deriveErrorDigest(error);

  return (
    <Empty
      className={cn(
        'w-full rounded-xl border border-solid border-destructive bg-destructive/10',
        className,
      )}
    >
      <EmptyHeader>
        <EmptyMedia className="relative">
          <div className="absolute inset-0 animate-pulse rounded-full bg-destructive/20" />
          <div className="relative rounded-full bg-destructive p-4">
            <AlertCircle className="size-8 text-destructive-foreground" />
          </div>
        </EmptyMedia>
        <EmptyTitle className="text-xl font-semibold text-destructive">
          {message ?? 'Une erreur est survenue'}
        </EmptyTitle>
        <EmptyDescription className="text-destructive/80">
          Veuillez réessayer, ou contacter le support si le problème persiste.
        </EmptyDescription>
      </EmptyHeader>
      <EmptyContent>
        <div className="flex flex-col items-center gap-2">
          <Button variant="outline" onClick={() => retry()}>
            <RefreshCw />
            Réessayer
          </Button>
          {digest && (
            <span className="font-mono text-xs text-destructive/80">
              Réf. {digest}
            </span>
          )}
        </div>
      </EmptyContent>
    </Empty>
  );
};

export const ErrorBoundary = catchError(ErrorFallback);
